<template>
  <v-hover v-slot:default="{ hover }">
    <v-card
      class="custom-lesson-card shadow half-radius"
      :class="hover ? 'custom-card-active' : ''"
    >
      <v-row no-gutters>
        <v-col cols="1">
          <v-icon color="#29284c" class="ml-3 mt-4">$vuetify.icons.appLesson</v-icon>
        </v-col>
        <v-col cols="11">
          <v-card-text class="custom-text pt-3 pb-0 ml-2">
            <v-row
              class="px-3"
              ref="row"
              :style="mounted && maxHeight ? `height: ${maxHeight}px` : ''"
            >
              <p class="headline mb-1">{{ item.name }}</p>
            </v-row>
            <span class="d-block caption">
              Step:
              <b>{{ teacher.completed }}</b>
            </span>
            <span class="d-block caption">
              Lesson Practice:
              <template>
                <b>{{ practices.completed }}</b>
                <span>/{{ practices.total - 1 }}</span>
              </template>
            </span>
            <span class="d-block caption">
              Sentence Structure:
              <template>
                <b>{{ grammars.completed }}</b>
                <span>/{{ grammars.total }}</span>
              </template>
            </span>
          </v-card-text>
          <v-card-actions class="ml-4">
            <v-btn
              min-width="80px"
              depressed
              :loading="stepLoading"
              @click="goLesson"
              height="22"
              class="text-capitalize caption pr-3 white--text"
              color="#FF347C"
              >Go</v-btn
            >
          </v-card-actions>
          <div
            class="mark-completed circle"
            :class="{ bordered: isTeacher && !progress.completed, rotating: loading }"
          >
            <v-tooltip top v-if="isTeacher">
              <template #activator="{ on }">
                <v-img
                  src="./../../../assets/completed.svg"
                  contain
                  class="invisible"
                  :class="{ hover: isTeacher, visible: !isTeacher || progress.completed }"
                  v-on="on"
                  @click="onClick"
                />
              </template>
              <span>{{ progress.completed ? "Unmark" : "Mark" }} lesson as completed</span>
            </v-tooltip>
            <v-img v-else-if="progress.completed" src="./../../../assets/completed.svg" contain />
          </div>
        </v-col>
      </v-row>
    </v-card>
  </v-hover>
</template>
<script>
/* eslint-disable no-console */
import {
  getLessonProgress,
  postLessonProgress,
  getLessonProgressById,
  patchLessonProgress,
} from "@/api/api";
import studentMixin from "@/mixins/studentMixin";

export default {
  props: {
    item: {
      required: true,
      type: Object,
    },
  },
  mixins: [studentMixin],
  data() {
    return {
      loading: false,
      stepLoading: false,
      mounted: false,
      progress: {},
    };
  },
  methods: {
    async goLesson() {
      this.stepLoading = true;
      this.$store.commit("modules/currentLesson", this.item);
      this.$store.commit("progress/lesson", this.progress);

      await this.$store.dispatch("lesson/teacher_lesson", this.item.teacher_lesson);
      await this.$store.dispatch("lesson/practice", this.item.practice);
      await this.$store.dispatch("lesson/grammar", this.item.grammar);
      this.$router.push({ name: "lesson-view" });
      this.stepLoading = false;
    },
    resolveHeight() {
      this.mounted = false;
      setTimeout(() => {
        this.$store.commit("modules/cardHeight", {
          [this.item.uuid]: this.$refs.row.getBoundingClientRect().height,
        });
        this.mounted = true;
      }, 66);
    },
    async getInitialData() {
      this.stepLoading = true;
      let response = await getLessonProgress({
        student__email: this.student.email,
        lesson__uuid: this.item.uuid,
      });
      let progress = null;
      if (response.data.count) {
        [progress] = response.data.results;
      } else {
        response = await postLessonProgress({
          lesson: this.item.uuid,
          module_progress: this.$store.getters["progress/module"].uuid,
          student: this.student.email,
          completed: false,
        });
        if (response) {
          progress = response.data;
        }
      }
      if (progress) {
        response = await getLessonProgressById(progress.uuid);
        this.progress = response.data;
      }
      this.stepLoading = false;
    },
    getCompleted(steps, iterable) {
      let completed = 0;
      for (let index = 0; index < steps.length; index++) {
        const element = iterable[steps[index]];
        if (
          (typeof element === "boolean" && element) ||
          (typeof element === "object" && element.completed)
        ) {
          completed++;
        }
      }
      return completed;
    },
    async onClick() {
      if (this.isTeacher) {
        this.loading = true;
        let body = {
          uuid: this.progress.uuid,
          completed: !this.progress.completed,
        };
        const response = await patchLessonProgress(body);
        if (response) {
          this.progress.completed = !this.progress.completed;
        }
        this.loading = false;
      }
    },
  },
  computed: {
    height() {
      return this.$store.getters["modules/cardHeight"];
    },
    maxHeight() {
      return this.$store.getters["modules/maxHeight"];
    },
    grammars() {
      let completed = 0;
      let total = 0;
      if (this.progress.grammar_progress) {
        total = this.progress.grammar_progress.exercises.length;
        this.progress.grammar_progress.exercises.forEach((element) => {
          if (element.completed) completed++;
        });
      }
      return {
        completed,
        total,
      };
    },
    practices() {
      let steps = [
        "audio_text_step",
        "audio_transcript_step",
        "pronunciation_step",
        "speaking_step",
        "vocabulary_step",
        "conclusion_step",
      ];
      let completed = 0;
      if (this.progress.practice_progress) {
        completed = this.getCompleted(steps, this.progress.practice_progress);
      }
      return {
        completed: completed,
        total: steps.length,
      };
    },
    teacher() {
      let steps = {
        intro_step: "Not Started",
        vocabulary_step: "Vocabulary",
        audio_text_step: "Listening",
        audio_transcript_step: "Pronunciation",
        grammar_step: "Grammar",
        speaking_step: "Speaking",
        conclusion_step: "Completed!",
      };
      let completed = 0;
      if (this.progress.teach_progress) {
        completed = this.getCompleted(Object.keys(steps), this.progress.teach_progress);
      }
      return {
        completed: Object.values(steps)[completed],
        total: 0,
      };
    },
    completedLesson() {
      return (
        this.grammars.completed === this.grammars.total &&
        this.practices.completed === this.practices.total - 1 &&
        this.teacher.completed === "Completed!"
      );
    },
  },
  mounted() {
    this.resolveHeight();
    this.getInitialData();
  },
  async created() {
    window.addEventListener("resize", this.resolveHeight, false);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resolveHeight);
    this.$store.commit("modules/cardHeight", { [this.item.uuid]: 0 });
  },
  watch: {
    "item.uuid"(val, old) {
      this.getInitialData();
      this.$store.commit("modules/cardHeight", { [old]: 0 });
      this.resolveHeight();
    },
  },
};
</script>
<style scoped>
.mark-completed {
  max-height: 26px;
  position: absolute;
  right: 6px;
  top: 6px;
}
.custom-lesson-card {
  min-height: 150px;
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #0000001a;
  opacity: 1;
  transition: all 0.2s linear;
}
.invisible {
  opacity: 0;
  transition: opacity 0.2s linear;
}
.invisible:hover {
  opacity: 1;
}
.visible {
  opacity: 1;
}
.bordered {
  border: 1px #29284c dashed;
  transition: border 0.2s linear;
}
.bordered:hover {
  border: 1px transparent dashed;
}
</style>
